import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Calculator,
  FileText,
  DollarSign,
  Building,
  ArrowRight,
  ExternalLink,
  CreditCard,
  Percent,
  PiggyBank,
  Home,
  Briefcase,
  Calendar,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import MainLayout from "@/components/layouts/MainLayout";

const Calculators = () => {
  // Current date for display
  const currentDate = new Date("April 20, 2025");
  const formattedDate = currentDate.toLocaleDateString("en-AU", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <>
      <Helmet>
        <title>Financial Calculators | Numberz Insight</title>
        <meta
          name="description"
          content="Access official Australian financial calculators for tax, GST, superannuation, loans and business planning. Updated with latest 2025 rates and information."
        />
        <meta
          name="keywords"
          content="financial calculators, tax calculator, GST calculator, superannuation calculator, loan calculator, business calculator, ATO calculators"
        />
      </Helmet>

      <MainLayout>
        {/* Hero Section */}
        <section className="relative py-10 md:py-16 lg:py-20 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2340&q=80"
              alt="Financial background"
              className="w-full h-full object-cover opacity-20 dark:opacity-10"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background/80 to-background"></div>
          </div>
          <div className="container relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-3 md:mb-4">
                  Financial Calculators
                </h1>
                <p className="text-base sm:text-lg md:text-xl text-muted-foreground mb-4 md:mb-6 px-2">
                  Access official calculators to help with your financial
                  planning and tax obligations
                </p>
                <div className="flex items-center justify-center gap-2 text-xs sm:text-sm text-muted-foreground mb-6 md:mb-8">
                  <Calendar className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span>Current as of {formattedDate}</span>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Calculators Tabs */}
        <section className="py-8 sm:py-12 md:py-16">
          <div className="container max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <Tabs defaultValue="tax" className="w-full">
              <TabsList className="flex w-full justify-between rounded-lg mb-6 md:mb-8 overflow-hidden bg-muted/50 p-1">
                <TabsTrigger
                  value="tax"
                  className="flex-1 text-xs sm:text-sm py-2 px-1 data-[state=active]:bg-background data-[state=active]:shadow-sm rounded-md transition-all"
                >
                  <DollarSign className="h-4 w-4 mx-auto sm:mx-0 sm:mr-2" />
                  <span className="hidden sm:inline">Tax</span>
                </TabsTrigger>
                <TabsTrigger
                  value="gst"
                  className="flex-1 text-xs sm:text-sm py-2 px-1 data-[state=active]:bg-background data-[state=active]:shadow-sm rounded-md transition-all"
                >
                  <Percent className="h-4 w-4 mx-auto sm:mx-0 sm:mr-2" />
                  <span className="hidden sm:inline">GST</span>
                </TabsTrigger>
                <TabsTrigger
                  value="super"
                  className="flex-1 text-xs sm:text-sm py-2 px-1 data-[state=active]:bg-background data-[state=active]:shadow-sm rounded-md transition-all"
                >
                  <PiggyBank className="h-4 w-4 mx-auto sm:mx-0 sm:mr-2" />
                  <span className="hidden sm:inline">Superannuation</span>
                </TabsTrigger>
                <TabsTrigger
                  value="loans"
                  className="flex-1 text-xs sm:text-sm py-2 px-1 data-[state=active]:bg-background data-[state=active]:shadow-sm rounded-md transition-all"
                >
                  <CreditCard className="h-4 w-4 mx-auto sm:mx-0 sm:mr-2" />
                  <span className="hidden sm:inline">Loans</span>
                </TabsTrigger>
                <TabsTrigger
                  value="business"
                  className="flex-1 text-xs sm:text-sm py-2 px-1 data-[state=active]:bg-background data-[state=active]:shadow-sm rounded-md transition-all"
                >
                  <Briefcase className="h-4 w-4 mx-auto sm:mx-0 sm:mr-2" />
                  <span className="hidden sm:inline">Business</span>
                </TabsTrigger>
              </TabsList>

              {/* Tax Calculators */}
              <TabsContent value="tax" className="space-y-6 md:space-y-8">
                <div className="relative mb-6 md:mb-8 rounded-lg overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1563986768609-322da13575f3?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2340&q=80"
                    alt="Tax calculator illustration"
                    className="w-full h-48 sm:h-64 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-background/90 via-background/50 to-transparent flex items-center">
                    <div className="p-6 md:p-8 max-w-md">
                      <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-2">
                        Tax Calculators
                      </h2>
                      <p className="text-sm sm:text-base text-muted-foreground">
                        Official tools to help you estimate your tax obligations
                        and plan your finances.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  <Card className="h-full">
                    <CardHeader>
                      <CardTitle className="flex items-center text-base sm:text-lg">
                        <Calculator className="h-4 w-4 sm:h-5 sm:w-5 mr-1.5 sm:mr-2 text-primary" />
                        Simple Tax Calculator
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-4">
                        Calculate the tax on your taxable income for the current
                        financial year.
                      </p>
                      <Badge className="text-xs mb-3 sm:mb-4">
                        Official ATO Tool
                      </Badge>
                    </CardContent>
                    <CardFooter>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full text-xs sm:text-sm"
                        asChild
                      >
                        <a
                          href="https://www.ato.gov.au/calculators-and-tools/tax-return-simple-tax-calculator"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Use Calculator{" "}
                          <ExternalLink className="ml-1 sm:ml-2 h-3 w-3 sm:h-4 sm:w-4" />
                        </a>
                      </Button>
                    </CardFooter>
                  </Card>

                  <Card className="h-full">
                    <CardHeader>
                      <CardTitle className="flex items-center">
                        <Calculator className="h-5 w-5 mr-2 text-primary" />
                        Income Tax Estimator
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground mb-4">
                        Work out your tax refund or debt estimate for the
                        current income year.
                      </p>
                      <Badge className="mb-4">Official ATO Tool</Badge>
                    </CardContent>
                    <CardFooter>
                      <Button variant="outline" className="w-full" asChild>
                        <a
                          href="https://www.ato.gov.au/calculators-and-tools/income-tax-estimator"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Use Calculator{" "}
                          <ExternalLink className="ml-2 h-4 w-4" />
                        </a>
                      </Button>
                    </CardFooter>
                  </Card>

                  <Card className="h-full">
                    <CardHeader>
                      <CardTitle className="flex items-center">
                        <Calculator className="h-5 w-5 mr-2 text-primary" />
                        Tax Withheld Calculator
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground mb-4">
                        Calculate how much tax to withhold from payments to your
                        employees or payees.
                      </p>
                      <Badge className="mb-4">Official ATO Tool</Badge>
                    </CardContent>
                    <CardFooter>
                      <Button variant="outline" className="w-full" asChild>
                        <a
                          href="https://www.ato.gov.au/calculators-and-tools/tax-withheld-calculator"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Use Calculator{" "}
                          <ExternalLink className="ml-2 h-4 w-4" />
                        </a>
                      </Button>
                    </CardFooter>
                  </Card>

                  <Card className="h-full">
                    <CardHeader>
                      <CardTitle className="flex items-center">
                        <Calculator className="h-5 w-5 mr-2 text-primary" />
                        Medicare Levy Calculator
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground mb-4">
                        Calculate your Medicare levy based on your taxable
                        income and circumstances.
                      </p>
                      <Badge className="mb-4">Official ATO Tool</Badge>
                    </CardContent>
                    <CardFooter>
                      <Button variant="outline" className="w-full" asChild>
                        <a
                          href="https://www.ato.gov.au/calculators-and-tools/tax-return-medicare-levy"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Use Calculator{" "}
                          <ExternalLink className="ml-2 h-4 w-4" />
                        </a>
                      </Button>
                    </CardFooter>
                  </Card>

                  <Card className="h-full">
                    <CardHeader>
                      <CardTitle className="flex items-center">
                        <Calculator className="h-5 w-5 mr-2 text-primary" />
                        Home Office Expenses Calculator
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground mb-4">
                        Calculate your deduction for working from home expenses.
                      </p>
                      <Badge className="mb-4">Official ATO Tool</Badge>
                    </CardContent>
                    <CardFooter>
                      <Button variant="outline" className="w-full" asChild>
                        <a
                          href="https://www.ato.gov.au/calculators-and-tools/home-office-expenses-calculator"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Use Calculator{" "}
                          <ExternalLink className="ml-2 h-4 w-4" />
                        </a>
                      </Button>
                    </CardFooter>
                  </Card>

                  <Card className="h-full">
                    <CardHeader>
                      <CardTitle className="flex items-center">
                        <Calculator className="h-5 w-5 mr-2 text-primary" />
                        Study and Training Loan Repayment Calculator
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground mb-4">
                        Estimate your study and training loan repayments based
                        on your income.
                      </p>
                      <Badge className="mb-4">Official ATO Tool</Badge>
                    </CardContent>
                    <CardFooter>
                      <Button variant="outline" className="w-full" asChild>
                        <a
                          href="https://www.ato.gov.au/calculators-and-tools/study-and-training-loan-repayment-calculator"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Use Calculator{" "}
                          <ExternalLink className="ml-2 h-4 w-4" />
                        </a>
                      </Button>
                    </CardFooter>
                  </Card>
                </div>
              </TabsContent>

              {/* GST Calculators */}
              <TabsContent value="gst" className="space-y-6 md:space-y-8">
                <div className="relative mb-6 md:mb-8 rounded-lg overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2340&q=80"
                    alt="GST calculator illustration"
                    className="w-full h-48 sm:h-64 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-background/90 via-background/50 to-transparent flex items-center">
                    <div className="p-6 md:p-8 max-w-md">
                      <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-2">
                        GST Calculators
                      </h2>
                      <p className="text-sm sm:text-base text-muted-foreground">
                        Tools to help you calculate and manage your GST
                        obligations.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  <Card className="h-full">
                    <CardHeader>
                      <CardTitle className="flex items-center text-base sm:text-lg">
                        <Calculator className="h-4 w-4 sm:h-5 sm:w-5 mr-1.5 sm:mr-2 text-primary" />
                        GST Calculator
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-4">
                        Calculate GST amounts for your business transactions.
                      </p>
                      <Badge className="text-xs mb-3 sm:mb-4">
                        Moneysmart Tool
                      </Badge>
                    </CardContent>
                    <CardFooter>
                      <Button variant="outline" className="w-full" asChild>
                        <a
                          href="https://moneysmart.gov.au/work-and-tax/gst-calculator"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Use Calculator{" "}
                          <ExternalLink className="ml-2 h-4 w-4" />
                        </a>
                      </Button>
                    </CardFooter>
                  </Card>

                  <Card className="h-full">
                    <CardHeader>
                      <CardTitle className="flex items-center text-base sm:text-lg">
                        <Calculator className="h-4 w-4 sm:h-5 sm:w-5 mr-1.5 sm:mr-2 text-primary" />
                        Business Activity Statement (BAS) Helper
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-4">
                        Get help with preparing and lodging your BAS.
                      </p>
                      <Badge className="text-xs mb-3 sm:mb-4">
                        Official ATO Resource
                      </Badge>
                    </CardContent>
                    <CardFooter>
                      <Button variant="outline" className="w-full" asChild>
                        <a
                          href="https://www.ato.gov.au/businesses-and-organisations/gst-excise-and-indirect-taxes/gst/reporting-and-paying-gst/business-activity-statements-(bas)"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View Resource{" "}
                          <ExternalLink className="ml-2 h-4 w-4" />
                        </a>
                      </Button>
                    </CardFooter>
                  </Card>

                  <Card className="h-full">
                    <CardHeader>
                      <CardTitle className="flex items-center text-base sm:text-lg">
                        <Calculator className="h-4 w-4 sm:h-5 sm:w-5 mr-1.5 sm:mr-2 text-primary" />
                        GST Registration Checker
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-4">
                        Determine if your business needs to register for GST
                        based on your turnover.
                      </p>
                      <Badge className="text-xs mb-3 sm:mb-4">
                        Official ATO Resource
                      </Badge>
                    </CardContent>
                    <CardFooter>
                      <Button variant="outline" className="w-full" asChild>
                        <a
                          href="https://www.ato.gov.au/businesses-and-organisations/gst-excise-and-indirect-taxes/gst/registering-for-gst"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View Resource{" "}
                          <ExternalLink className="ml-2 h-4 w-4" />
                        </a>
                      </Button>
                    </CardFooter>
                  </Card>
                </div>
              </TabsContent>

              {/* Superannuation Calculators */}
              <TabsContent value="super" className="space-y-6 md:space-y-8">
                <div className="relative mb-6 md:mb-8 rounded-lg overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1621951753015-740c699ab970?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2340&q=80"
                    alt="Superannuation calculator illustration"
                    className="w-full h-48 sm:h-64 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-background/90 via-background/50 to-transparent flex items-center">
                    <div className="p-6 md:p-8 max-w-md">
                      <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-2">
                        Superannuation Calculators
                      </h2>
                      <p className="text-sm sm:text-base text-muted-foreground">
                        Plan for your retirement with these superannuation
                        calculation tools.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  <Card className="h-full">
                    <CardHeader>
                      <CardTitle className="flex items-center text-base sm:text-lg">
                        <Calculator className="h-4 w-4 sm:h-5 sm:w-5 mr-1.5 sm:mr-2 text-primary" />
                        Superannuation Calculator
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-4">
                        Estimate how much super you'll have when you retire and
                        how fees affect your balance.
                      </p>
                      <Badge className="text-xs mb-3 sm:mb-4">
                        Moneysmart Tool
                      </Badge>
                    </CardContent>
                    <CardFooter>
                      <Button variant="outline" className="w-full" asChild>
                        <a
                          href="https://moneysmart.gov.au/how-super-works/superannuation-calculator"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Use Calculator{" "}
                          <ExternalLink className="ml-2 h-4 w-4" />
                        </a>
                      </Button>
                    </CardFooter>
                  </Card>

                  <Card className="h-full">
                    <CardHeader>
                      <CardTitle className="flex items-center text-base sm:text-lg">
                        <Calculator className="h-4 w-4 sm:h-5 sm:w-5 mr-1.5 sm:mr-2 text-primary" />
                        Super Guarantee Contributions Calculator
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-4">
                        Calculate the super guarantee contributions you need to
                        make for your employees.
                      </p>
                      <Badge className="text-xs mb-3 sm:mb-4">
                        Official ATO Tool
                      </Badge>
                    </CardContent>
                    <CardFooter>
                      <Button variant="outline" className="w-full" asChild>
                        <a
                          href="https://www.ato.gov.au/calculators-and-tools/super-guarantee-contributions"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Use Calculator{" "}
                          <ExternalLink className="ml-2 h-4 w-4" />
                        </a>
                      </Button>
                    </CardFooter>
                  </Card>

                  <Card className="h-full">
                    <CardHeader>
                      <CardTitle className="flex items-center text-base sm:text-lg">
                        <Calculator className="h-4 w-4 sm:h-5 sm:w-5 mr-1.5 sm:mr-2 text-primary" />
                        Retirement Planner
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-4">
                        Estimate how much money you'll have in retirement,
                        including super and the Age Pension.
                      </p>
                      <Badge className="text-xs mb-3 sm:mb-4">
                        Moneysmart Tool
                      </Badge>
                    </CardContent>
                    <CardFooter>
                      <Button variant="outline" className="w-full" asChild>
                        <a
                          href="https://moneysmart.gov.au/retirement-income/retirement-planner"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Use Calculator{" "}
                          <ExternalLink className="ml-2 h-4 w-4" />
                        </a>
                      </Button>
                    </CardFooter>
                  </Card>

                  <Card className="h-full">
                    <CardHeader>
                      <CardTitle className="flex items-center text-base sm:text-lg">
                        <Calculator className="h-4 w-4 sm:h-5 sm:w-5 mr-1.5 sm:mr-2 text-primary" />
                        YourSuper Comparison Tool
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-4">
                        Compare MySuper products based on fees and performance.
                      </p>
                      <Badge className="text-xs mb-3 sm:mb-4">
                        Official ATO Tool
                      </Badge>
                    </CardContent>
                    <CardFooter>
                      <Button variant="outline" className="w-full" asChild>
                        <a
                          href="https://www.ato.gov.au/calculators-and-tools/super-yoursuper-comparison-tool"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Use Calculator{" "}
                          <ExternalLink className="ml-2 h-4 w-4" />
                        </a>
                      </Button>
                    </CardFooter>
                  </Card>
                </div>
              </TabsContent>

              {/* Loan Calculators */}
              <TabsContent value="loans" className="space-y-6 md:space-y-8">
                <div className="relative mb-6 md:mb-8 rounded-lg overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1560520031-3a4dc4e9de0c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2340&q=80"
                    alt="Loan calculator illustration"
                    className="w-full h-48 sm:h-64 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-background/90 via-background/50 to-transparent flex items-center">
                    <div className="p-6 md:p-8 max-w-md">
                      <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-2">
                        Loan Calculators
                      </h2>
                      <p className="text-sm sm:text-base text-muted-foreground">
                        Calculate repayments and compare loan options with these
                        financial tools.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  <Card className="h-full">
                    <CardHeader>
                      <CardTitle className="flex items-center text-base sm:text-lg">
                        <Calculator className="h-4 w-4 sm:h-5 sm:w-5 mr-1.5 sm:mr-2 text-primary" />
                        Mortgage Calculator
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-4">
                        Calculate your home loan repayments and see the effect
                        of extra repayments.
                      </p>
                      <Badge className="text-xs mb-3 sm:mb-4">
                        Moneysmart Tool
                      </Badge>
                    </CardContent>
                    <CardFooter>
                      <Button variant="outline" className="w-full" asChild>
                        <a
                          href="https://moneysmart.gov.au/home-loans/mortgage-calculator"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Use Calculator{" "}
                          <ExternalLink className="ml-2 h-4 w-4" />
                        </a>
                      </Button>
                    </CardFooter>
                  </Card>

                  <Card className="h-full">
                    <CardHeader>
                      <CardTitle className="flex items-center text-base sm:text-lg">
                        <Calculator className="h-4 w-4 sm:h-5 sm:w-5 mr-1.5 sm:mr-2 text-primary" />
                        Personal Loan Calculator
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-4">
                        Calculate your personal loan repayments and compare loan
                        options.
                      </p>
                      <Badge className="text-xs mb-3 sm:mb-4">
                        Moneysmart Tool
                      </Badge>
                    </CardContent>
                    <CardFooter>
                      <Button variant="outline" className="w-full" asChild>
                        <a
                          href="https://moneysmart.gov.au/loans/personal-loan-calculator"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Use Calculator{" "}
                          <ExternalLink className="ml-2 h-4 w-4" />
                        </a>
                      </Button>
                    </CardFooter>
                  </Card>

                  <Card className="h-full">
                    <CardHeader>
                      <CardTitle className="flex items-center text-base sm:text-lg">
                        <Calculator className="h-4 w-4 sm:h-5 sm:w-5 mr-1.5 sm:mr-2 text-primary" />
                        Credit Card Calculator
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-4">
                        Calculate how long it will take to pay off your credit
                        card and how much interest you'll pay.
                      </p>
                      <Badge className="text-xs mb-3 sm:mb-4">
                        Moneysmart Tool
                      </Badge>
                    </CardContent>
                    <CardFooter>
                      <Button variant="outline" className="w-full" asChild>
                        <a
                          href="https://moneysmart.gov.au/credit-cards/credit-card-calculator"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Use Calculator{" "}
                          <ExternalLink className="ml-2 h-4 w-4" />
                        </a>
                      </Button>
                    </CardFooter>
                  </Card>

                  <Card className="h-full">
                    <CardHeader>
                      <CardTitle className="flex items-center text-base sm:text-lg">
                        <Calculator className="h-4 w-4 sm:h-5 sm:w-5 mr-1.5 sm:mr-2 text-primary" />
                        Mortgage Switching Calculator
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-4">
                        Calculate whether switching your home loan will save you
                        money.
                      </p>
                      <Badge className="text-xs mb-3 sm:mb-4">
                        Moneysmart Tool
                      </Badge>
                    </CardContent>
                    <CardFooter>
                      <Button variant="outline" className="w-full" asChild>
                        <a
                          href="https://moneysmart.gov.au/home-loans/mortgage-switching-calculator"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Use Calculator{" "}
                          <ExternalLink className="ml-2 h-4 w-4" />
                        </a>
                      </Button>
                    </CardFooter>
                  </Card>
                </div>
              </TabsContent>

              {/* Business Calculators */}
              <TabsContent value="business" className="space-y-6 md:space-y-8">
                <div className="relative mb-6 md:mb-8 rounded-lg overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1664575599736-c5197c684128?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2340&q=80"
                    alt="Business calculator illustration"
                    className="w-full h-48 sm:h-64 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-background/90 via-background/50 to-transparent flex items-center">
                    <div className="p-6 md:p-8 max-w-md">
                      <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-2">
                        Business Calculators
                      </h2>
                      <p className="text-sm sm:text-base text-muted-foreground">
                        Tools to help you assess and manage your business
                        finances.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  <Card className="h-full">
                    <CardHeader>
                      <CardTitle className="flex items-center text-base sm:text-lg">
                        <Calculator className="h-4 w-4 sm:h-5 sm:w-5 mr-1.5 sm:mr-2 text-primary" />
                        Business Viability Assessment Tool
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-4">
                        Assess the viability of your business and identify areas
                        for improvement.
                      </p>
                      <Badge className="text-xs mb-3 sm:mb-4">
                        Official ATO Tool
                      </Badge>
                    </CardContent>
                    <CardFooter>
                      <Button variant="outline" className="w-full" asChild>
                        <a
                          href="https://www.ato.gov.au/calculators-and-tools/business-viability-assessment-tool/"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Use Calculator{" "}
                          <ExternalLink className="ml-2 h-4 w-4" />
                        </a>
                      </Button>
                    </CardFooter>
                  </Card>

                  <Card className="h-full">
                    <CardHeader>
                      <CardTitle className="flex items-center text-base sm:text-lg">
                        <Calculator className="h-4 w-4 sm:h-5 sm:w-5 mr-1.5 sm:mr-2 text-primary" />
                        Company Tax Calculator
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-4">
                        Calculate your company tax based on your business income
                        and deductions.
                      </p>
                      <Badge className="text-xs mb-3 sm:mb-4">
                        Official ATO Resource
                      </Badge>
                    </CardContent>
                    <CardFooter>
                      <Button variant="outline" className="w-full" asChild>
                        <a
                          href="https://www.ato.gov.au/businesses-and-organisations/income-tax-for-businesses/working-out-your-income-tax/tax-rates-for-businesses"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View Resource{" "}
                          <ExternalLink className="ml-2 h-4 w-4" />
                        </a>
                      </Button>
                    </CardFooter>
                  </Card>

                  <Card className="h-full">
                    <CardHeader>
                      <CardTitle className="flex items-center text-base sm:text-lg">
                        <Calculator className="h-4 w-4 sm:h-5 sm:w-5 mr-1.5 sm:mr-2 text-primary" />
                        PAYG Installment Calculator
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-4">
                        Calculate your PAYG installment amount based on your
                        business income.
                      </p>
                      <Badge className="text-xs mb-3 sm:mb-4">
                        Official ATO Resource
                      </Badge>
                    </CardContent>
                    <CardFooter>
                      <Button variant="outline" className="w-full" asChild>
                        <a
                          href="https://www.ato.gov.au/businesses-and-organisations/income-tax-for-businesses/pay-as-you-go-(payg)-instalments"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View Resource{" "}
                          <ExternalLink className="ml-2 h-4 w-4" />
                        </a>
                      </Button>
                    </CardFooter>
                  </Card>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </section>

        {/* Disclaimer Section */}
        <section className="bg-muted/30 dark:bg-muted/10 py-8 sm:py-10 md:py-12">
          <div className="container max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-card dark:bg-gray-800 border border-border rounded-lg p-4 sm:p-6 shadow-sm">
              <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">
                Important Information
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground mb-3 sm:mb-4">
                All calculators linked on this page are provided by official
                government sources and are current as of {formattedDate}. These
                calculators are for general information purposes only and should
                not be considered as financial advice.
              </p>
              <p className="text-sm sm:text-base text-muted-foreground">
                For personalized financial advice, please consult with a
                qualified financial advisor or contact our team for professional
                assistance with your specific situation.
              </p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-10 sm:py-12 md:py-16">
          <div className="container max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h2 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4">
                Need Professional Assistance?
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground mb-4 sm:mb-6 max-w-2xl mx-auto px-2">
                Our team of expert accountants and tax professionals can help
                you navigate complex financial matters and optimize your tax
                position.
              </p>
              <div className="flex flex-col xs:flex-row gap-3 sm:gap-4 justify-center">
                <Button
                  size="sm"
                  className="w-full xs:w-auto sm:size-default"
                  asChild
                >
                  <Link to="/contact">
                    Contact Us{" "}
                    <ArrowRight className="ml-1 sm:ml-2 h-3 w-3 sm:h-4 sm:w-4" />
                  </Link>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full xs:w-auto sm:size-default"
                  asChild
                >
                  <Link to="/tax-information">
                    View Tax Information{" "}
                    <ArrowRight className="ml-1 sm:ml-2 h-3 w-3 sm:h-4 sm:w-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </MainLayout>
    </>
  );
};

export default Calculators;
